import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { costContractStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import { CONTRACT_KIND_KEY, CONTRACT_STATUS_KEY, type ContractGroup } from '../lib/contracts'
import { formatMoney, isNegativeMoney } from '../lib/money'

interface Props { groups: ContractGroup[]; grouped: boolean; currency: string; locale: string; onOpen: (id: string) => void }

const ContractsTable: React.FC<Props> = ({ groups, grouped, currency, locale, onOpen }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">{t('CostControl_contract_field_number')}</TableHead>
            <TableHead>{t('CostControl_contract_field_name')}</TableHead>
            <TableHead>{t('CostControl_contract_field_counterparty')}</TableHead>
            <TableHead className="w-28">{t('CostControl_contract_field_status')}</TableHead>
            <TableHead className="w-40 text-right">{t('CostControl_contract_current_value')}</TableHead>
            <TableHead className="w-40 text-right">{t('CostControl_contract_invoiced')}</TableHead>
            <TableHead className="w-40 text-right">{t('CostControl_contract_remaining')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {grouped && (
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell colSpan={7} className="py-1.5 text-xs font-semibold text-muted-foreground">
                    {g.label} · {g.contracts.length}
                  </TableCell>
                </TableRow>
              )}
              {g.contracts.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                  <TableCell className="font-mono text-xs">{c.number ?? '—'}</TableCell>
                  <TableCell>
                    <button type="button" className="text-left font-medium text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onOpen(c.id) }}>
                      {c.name}
                    </button>
                    {c.kind === 'MAIN' && <Badge variant="secondary" className="ml-2">{t(CONTRACT_KIND_KEY.MAIN)}</Badge>}
                  </TableCell>
                  <TableCell>{c.counterpartyName}</TableCell>
                  <TableCell><Badge variant={costContractStatusVariant(c.status)}>{t(CONTRACT_STATUS_KEY[c.status])}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{money(c.totals.currentValue)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(c.totals.invoiced)}</TableCell>
                  <TableCell className={cn('text-right tabular-nums', isNegativeMoney(c.totals.remaining) && 'text-destructive')}>{money(c.totals.remaining)}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
export default ContractsTable
